import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PARENT_COMPANY, ROOT_API_URL } from "../config/defaultSettings";
import { getAppType } from "../utils";
import { toast } from "react-toastify";
import { isServerVersionNewer } from "../utils/versionCompare";

interface ApiErrorResponse {
  code: string;
  message: string;
  status: number;
}

interface ApiSuccessResponse {
  status: "success" | "error";
  data?: any;
}

let isRefreshing = false;
let isLogoutInProgress = false;
let hasClearedAuth = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isAuthDebugEnabled = () =>
  typeof window !== "undefined" &&
  window.localStorage?.getItem("debug-auth") === "true";

const debugAuthLog = (...args: any[]) => {
  if (isAuthDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.log("[auth-debug]", ...args);
  }
};

const clearAuthData = async (showSessionExpiredToast = true) => {
  if (isLogoutInProgress || hasClearedAuth) {
    debugAuthLog("clearAuthData: skipped", {
      isLogoutInProgress,
      hasClearedAuth,
    });
    return;
  }
  isLogoutInProgress = true;
  hasClearedAuth = true;
  debugAuthLog("clearAuthData: start");
  try {
    // Try to call logout endpoint to clean up server-side session
    await fetch(`${ROOT_API_URL}/account/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch (error) {
    console.error("Error during logout:", error);
    // Don't fail if logout request fails - continue with client-side cleanup
  } finally {
    isLogoutInProgress = false;
    debugAuthLog("clearAuthData: done");
  }

  // Show session expired notification to user
  if (showSessionExpiredToast) {
    toast.warning("Your session has expired. Please log in again.", {
      toastId: "session-expired",
      autoClose: 7000,
    });
  }

  // Client-side token cleanup will be handled by the auth hook

  // Let the auth hook handle the cleanup to avoid conflicts
  // The useAuth hook will handle localStorage cleanup and navigation
};

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const requestUrl = typeof args === "string" ? args : (args as any)?.url || "";
  const baseQuery = fetchBaseQuery({
    baseUrl: ROOT_API_URL,
    credentials: "include",
    prepareHeaders: (headers) => {
      headers.set("x-client-type", "web");
      headers.set("x-app-type", getAppType());

      // Only set parent company if it exists
      const parentCompany = localStorage.getItem(PARENT_COMPANY);
      if (parentCompany && parentCompany !== "null") {
        headers.set("x-parent-company", parentCompany);
        headers.set("customer-context", parentCompany);
      }

      return headers;
    },
  });

  let result = await baseQuery(args, api, extraOptions);

  if (
    (result.data as ApiSuccessResponse)?.status === "success" &&
    (String(requestUrl).includes("/account/login") ||
      String(requestUrl).includes("/account/refresh") ||
      String(requestUrl).includes("/account/current"))
  ) {
    hasClearedAuth = false;
    debugAuthLog("auth success reset", requestUrl);
  }

  // Version check - store current version in localStorage
  const currentVersion =
    typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "";

  if (result.meta?.response?.headers && currentVersion) {
    try {
      const serverVersion = result.meta.response.headers.get("x-version");

      if (isServerVersionNewer(serverVersion, currentVersion)) {
        window.localStorage.setItem("version-update-needed", "true");
      }
      window.localStorage.setItem("version", currentVersion);
    } catch (err) {
      console.error("Version Check Error:", err);
    }
  }

  // Handle 401/403 errors
  if (result.error?.status === 401 || result.error?.status === 403) {
    const errorCode = (result.error?.data as ApiErrorResponse)?.code;
    const errorMessage =
      String((result.error as any)?.data?.message || "").toLowerCase();
    const isAccountLogout = String(requestUrl).includes("/account/logout");
    const isMissingAuth =
      !errorCode &&
      (errorMessage.includes("no token") ||
        errorMessage.includes("missing token") ||
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("authentication failed"));
    const isAccountCurrent = String(requestUrl).includes("/account/current");

    /**
     * IMPORTANT:
     * Not every 403 is an auth/session failure (ex: feature flags, RBAC, tenant policy).
     * Only clear auth on known auth/token codes.
     */
    const shouldClearAuth =
      errorCode === "NO_TOKEN" ||
      errorCode === "TOKEN_EXPIRED" ||
      errorCode === "TOKEN_INVALID_WITH_REFRESH" ||
      errorCode === "AUTHENTICATION_FAILED" ||
      isMissingAuth ||
      (result.error?.status === 403 && isAccountCurrent);

    // If we don't have a recognized auth error code, do NOT log the user out.
    // Let callers/UI handle 403s like FEATURE_DISABLED / NOT_AUTHORIZED, etc.
    debugAuthLog("auth error", {
      requestUrl,
      status: result.error?.status,
      errorCode,
      errorMessage,
      shouldClearAuth,
      hasClearedAuth,
      isAccountLogout,
    });

    if (!shouldClearAuth || hasClearedAuth || isAccountLogout) {
      return result;
    }

    // If there's no token at all, don't try to refresh - just clear auth
    if (errorCode === "NO_TOKEN") {
      await clearAuthData();
      return result;
    }

    // Only try refresh if we get a TOKEN_EXPIRED or TOKEN_INVALID_WITH_REFRESH error
    if (
      errorCode === "TOKEN_EXPIRED" ||
      errorCode === "TOKEN_INVALID_WITH_REFRESH"
    ) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshResult = await baseQuery(
            {
              url: "/account/refresh",
              method: "POST",
              credentials: "include", // Ensure cookies are sent
            },
            api,
            extraOptions
          );

          if (
            (refreshResult.data as ApiSuccessResponse)?.status === "success"
          ) {
            hasClearedAuth = false;
            processQueue(null, refreshResult.data);
            // Retry original request
            result = await baseQuery(args, api, extraOptions);
          } else {
            processQueue(new Error("Token refresh failed"));
            await clearAuthData();
          }
        } catch (error) {
          console.error("Error during token refresh:", error);
          processQueue(error);
          await clearAuthData();
        } finally {
          isRefreshing = false;
        }
      } else {
        // Wait for the refresh to complete
        try {
          await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          // Retry original request after refresh
          result = await baseQuery(args, api, extraOptions);
        } catch (error) {
          console.error("Error processing queued request:", error);
          await clearAuthData();
        }
      }
    } else {
      // Handle other authentication failures (AUTHENTICATION_FAILED, etc.)
      await clearAuthData();
    }
  }

  // Tier / seat caps (P3): hard enforcement returns 402 with TIER_LIMIT — not an auth error.
  if (result.error?.status === 402) {
    const errBody = result.error?.data as ApiErrorResponse & {
      kind?: string;
    };
    if (errBody?.code === "TIER_LIMIT") {
      toast.info(
        errBody.message || "Plan limit reached. Upgrade to add more.",
        {
          toastId: `tier-limit-${errBody.kind || "cap"}`,
          autoClose: 8000,
        }
      );
    }
  }

  return result;
};

const tagTypes = [
  "User",
  "Auth",
  "Jobs",
  "CurrentUser",
  "Clients",
  "Drivers",
  "Config",
  "EmailConfig",
  "Customers",
  "Dispatch",
  "Items",
  "Drivers",
  "Employees",
  "Organizations",
  "Equipment",
  "Inventory",
  "Invoices",
  "InvoicesV2",
  "Items",
  "JobItems",
  "Timeline",
  "Legs",
  "Locations",
  "Manifests",
  "Media",
  "Notifications",
  "Payouts",
  "Templates",
  "PdfTemplates",
  "Qr",
  "Reports",
  "Roles",
  "Permissions",
  "Schedule",
  "Services",
  "Shift",
  "Tasks",
  "Tags",
  "JobTasks",
  "LegTasks",
  "TaskTemplates",
  "TaskTemplateGroups",
  "Chat",
  "ChatMessages",
  "AdminDashboard",
  "Leads",
  "Users",
  "GoogleDrive",
];

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes,
  endpoints: () => ({}),
});

export const clearApiCache = () => {
  return api.util.resetApiState();
};

export const { usePrefetch } = api;
